/**
 * The shared-funder map and funder discovery as what they are: computed
 * overlays with a recipe, a version, parameters, and a run, measured against
 * the next year's filings on a held-out split. Written for the grant seeker
 * who wants to know what a map is telling them, and for the researcher who
 * wants the method and the numbers before trusting a recommendation.
 */
import type { DocPage } from '../types.js';

export const mapPage: DocPage = {
  slug: 'map',
  title: 'The shared-funder map and funder discovery',
  audience: 'grant seekers, researchers',
  promise: 'What the map computes and from what, why intermediaries are set aside, how the recipes were validated against the next year’s filings, and what a map does not claim.',
  lead: 'Two organizations are neighbours on the map when the same funders gave to both within a window of years. That is computed from grant rows and nothing else. This page says how, with what parameters, how well it predicted the next year’s funders on a held-out split, and what it cannot tell you.',
  sections: [
    {
      id: 'recipe',
      heading: 'The recipe',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The co-funding recipe takes an anchor organization and the funders that gave to it within a three-year window of tax years, and finds the other organizations those funders also gave to. Each such organization is scored by the number of funders it shares with the anchor; organizations sharing at least two are members, at most fifty of them, best first. The map page shows the anchor, its counted funders, the members, and which funders each member shares, and names the recipe, its version, its parameters, and the run that produced it, so the same map can be recomputed from the same rows.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'A funder that reaches more than a thousand distinct organizations in the window is set aside: it is shown as reaching the anchor, but it is not counted toward any shared score. Donor-advised fund sponsors, the largest community foundations, and corporate giving programs reach tens of thousands of organizations and share recipients with everyone; as evidence that two organizations do related work, they are noise, and before the ceiling existed the recipe’s cost was the square of their reach. The ceiling is the recipe’s second version; the first counted every funder.',
          ],
        },
      ],
    },
    {
      id: 'discovery',
      heading: 'Funder discovery',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A second recipe, funder discovery, asks a different question: which funders give to organizations whose filed words read like this one’s? It takes the anchor’s mission, finds the twenty-five nearest texts in the anchor’s state among the missions, program descriptions, and grant purposes that lead to a funder, and scores each funder by the summed similarity of the texts that lead to it. Texts that lead to no funder are not searched, since they could name nobody; that rule is the recipe’s second version. It depends on embeddings of every organization’s text, which the national build has carried since 19 October 2026; the recipe is computed for every organization with a mission, about 506,000 of them, and kept in the graph with its provenance, but no page shows it yet. The shared-funder map, which needs only the grant rows, is shown.',
          ],
        },
      ],
    },
    {
      id: 'validated',
      heading: 'How the recipes were validated',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A recommendation is a funder a recipe names for an anchor that does not already fund it in the years through some year N. It is a hit if that funder’s first grant to the anchor appears in a return for year N+1. Precision at k is hits over the first k recommendations per anchor; recall is hits over the anchor’s new funders; chance is what a recommender drawing at random from the funders active through N would score over the same slots. A recipe that does not beat chance is not recommending.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'On the fixture snapshot, one state’s filings, with recipes computed through tax year 2024 and scored against 2025, the second version of co-funding recommends at 6.0 percent precision at the first slot against 1.4 percent chance, and at ten slots 2.6 percent against 1.5 percent with 77 percent recall; computed through 2023 and scored against 2024, 6.3 percent at three slots against 2.8 percent. The ceiling halved the number of anchors with a map on that snapshot (409 to 184 through 2024), because the snapshot’s own community foundations fall under it, while precision held or rose: the recommendations the hubs carried were ones a reader could have made without a graph. Funder discovery had seven anchors and eleven recommendations on the snapshot and is reported as unmeasured.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'At national scale, measured in October 2026 on the production tables through tax year 2024 against the funders that first appear in 2025: co-funding names a funder that turns out to be new for 11.2 percent of first-slot recommendations over 89,671 organizations, and finds 16.4 percent of the new funders in ten slots; funder discovery version 2 names one for 1.4 percent of first-slot recommendations over 379,767 organizations, 350 times chance and an eighth of the shared-funder structure, and finds 9.5 percent in ten slots. The map is the better recommender for an organization that has funders; discovery is the only one for an organization that has none yet. Recall is a floor in every case: a funder whose next return has not been filed or released is invisible to the measure. The full report, with every table, is the validation document the project keeps with its measurements.',
          ],
        },
      ],
    },
    {
      id: 'reading',
      heading: 'What a map does not claim',
      blocks: [
        {
          kind: 'list',
          items: [
            ['It does not say two organizations coordinate, agree, or know of each other. It says the same funders gave to both.'],
            ['It does not say two organizations do the same work. Funders give across subjects, and a shared funder is shared money, not shared purpose.'],
            ['It carries no label of any kind. Where a reader sees a political or ideological structure, it is a pattern of co-funding the reader has read into the rows, with the recipe and its parameters beside it, never an attribute the site assigns.'],
            ['It is only as complete as the filings in the window. A funder whose return for the latest year has not been released is not on the map yet.'],
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'Every map is a computed fact with provenance: the recipe, its version, its parameters, and the run identifier are on the page, and a reader who wants the rows can trace each shared funder to the return that reported each grant. ',
            { link: '/about/reading#map', text: 'The reading page' },
            ' says the same in fewer words beside the map itself.',
          ],
        },
      ],
    },
  ],
};
